import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  DestroyRef,
  ElementRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { GetMediaService } from '@app/data/media/get-media.service';
import { DeleteMediaService } from '@app/data/media/delete-media.service';
import { DatabaseCacheService } from '@app/data/cache/database-cache.service';
import { MediaDocument } from '@app/data/models/media/media.model';
import { RecordData, TypesUtils } from '../../../../types';
import { ConfirmDialogComponent } from '@app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { DetailsHeaderComponent } from '../../measured-data-details/components/shared/details-header/details-header.component';
import { MediaVideoPlayerComponent } from '../ui/media-video-player.component';
import { PopupWindowStateService } from '../../expert-dialog/popup-window/popup-window-state.service';
import {
  createMkplayerInstance,
  destroyMkplayerInstance,
  PLAYER_LOAD_TIMEOUT_MS,
  PLAYER_RETRY_INTERVAL_MS,
  PLAYER_RETRY_MAX,
} from '../utils/mkplayer.utils';
import { buildMediaThumbnailUrl } from '@app/shared/utils/media-thumbnail.utils';

const METHOD_BADGE_COLORS: Record<string, string> = {
  VE: '#8b0000',
  VF: '#6a1b9a',
  VS: '#daa520',
  Others: '#757575',
};

@Component({
  selector: 'app-media-detail',
  templateUrl: './media-detail.component.html',
  styleUrls: ['./media-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    DetailsHeaderComponent,
    MediaVideoPlayerComponent,
  ],
})
export class MediaDetailComponent implements OnInit, AfterViewInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly getMediaService = inject(GetMediaService);
  private readonly deleteMediaService = inject(DeleteMediaService);
  private readonly common = inject(DatabaseCacheService);
  private readonly popupWindowState = inject(PopupWindowStateService);

  @ViewChild('mkplayerdiv') mkplayerdiv?: ElementRef<HTMLElement>;

  readonly media = signal<MediaDocument | null>(null);
  readonly loading = signal(true);
  readonly error = signal(false);

  readonly title = computed(() => {
    const m = this.media();
    if (!m) return $localize`:@@home.mediaDetail.title.default:メディア詳細`;
    const typeLabel = m.data.mediaType === 'Video'
      ? $localize`:@@home.mediaDetail.title.video:動画`
      : $localize`:@@home.mediaDetail.title.image:静止画`;
    return `${m.data.method}｜${typeLabel}`;
  });

  readonly badgeText = computed(() => {
    const m = this.media();
    if (!m) return '';
    return m.data.mediaType === 'Video'
      ? $localize`:@@home.mediaDetail.badge.video:動画`
      : $localize`:@@home.mediaDetail.badge.image:静止画`;
  });

  readonly badgeColor = computed(() => {
    const m = this.media();
    return m ? (METHOD_BADGE_COLORS[m.data.method] ?? '#757575') : '#757575';
  });

  readonly measureDate = computed(() => {
    const m = this.media();
    if (!m?.data.createdAt) return '';
    const d = new Date(m.data.createdAt);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}/${mo}/${day} ${h}:${min}`;
  });

  readonly imageUrl = computed(() => {
    const m = this.media();
    if (!m || m.data.mediaType !== 'Image') return '';
    const s = m.data.storage;
    return `${s.serverRootURL}/${s.container}/${s.blobName}${s.blobSasToken}`;
  });

  readonly thumbnailUrl = computed(() => {
    const m = this.media();
    if (!m || m.data.mediaType !== 'Video') return '';
    // サムネ未生成（必須欠落 or thumbnailName に動画ファイル名が流用されている）の場合は
    // 空文字を返し、無効 URL への 404 を抑止する。判定は共有ユーティリティに集約。
    return buildMediaThumbnailUrl(m.data.storage);
  });

  /** mediaStatus がエラー系（Failed / Error / Canceled） */
  readonly isStatusError = computed(() => {
    const status = this.media()?.data?.mediaStatus?.toLowerCase() ?? '';
    return status === 'failed' || status === 'error' || status === 'canceled' || status === 'cancelled';
  });

  /** mediaStatus が Deleted（削除済み） */
  readonly isStatusDeleted = computed(
    () => (this.media()?.data?.mediaStatus?.toLowerCase() ?? '') === 'deleted',
  );

  readonly linkedRecord = signal<RecordData | null>(null);

  playerInstance: any = null;
  private playerTimer: ReturnType<typeof setTimeout> | null = null;
  private playerRetryCount = 0;
  private mediaId = '';
  private sectionId = '';
  /** Where the user navigated from: 'records' | 'medias' | 'dialogs' (default: medias) */
  private fromContext: 'records' | 'medias' | 'dialogs' = 'medias';

  ngOnInit(): void {
    // Read `from` query param to determine back navigation target
    const fromParam = this.route.snapshot.queryParamMap.get('from');
    this.fromContext = fromParam === 'records'
      ? 'records'
      : fromParam === 'dialogs'
        ? 'dialogs'
        : 'medias';

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.mediaId = params['mediaId'];
      this.common.setSelectedUserId(params['userId']);
      this.popupWindowState.setCurrentRecordId(this.mediaId ?? '');
      const section = this.common.getSelectedSection();
      this.sectionId = section?.sectionId ?? '';
      this.loadMedia();
    });

    this.destroyRef.onDestroy(() => {
      this.destroyPlayer();
    });
  }

  ngAfterViewInit(): void {
    // プレーヤー初期化はメディアロード完了後に行う
    this.route.fragment.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((fragment) => {
      if (fragment) {
        this.popupWindowState.setCurrentFragment(fragment);
        this.popupWindowState.setExpanded(true);
      }
    });
  }

  goBack(): void {
    const section = this.common.getSelectedSection();
    const userId = this.common.getSelectedUserId();
    if (section && userId) {
      const target = this.fromContext === 'records'
        ? 'records'
        : this.fromContext === 'dialogs'
          ? 'dialogs'
          : 'medias';
      this.router.navigate(['/home', section.sectionId, 'users', userId, target]);
    }
  }

  confirmDelete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: false,
      data: {
        title: $localize`:@@home.mediaDetail.dialog.deleteTitle:メディアの削除`,
        message: $localize`:@@home.mediaDetail.dialog.deleteMessage:このメディアを本当に削除しますか？`,
      },
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result) {
        this.deleteMediaService.deleteMedia(this.mediaId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.common.evictUserMediaFromCache(this.mediaId);
          this.snackBar.open(
            $localize`:@@home.mediaDetail.snackbar.deleted:選択されたメディアを削除しました`,
            $localize`:@@home.mediaDetail.snackbar.close:閉じる`,
            { duration: 3000 },
          );
          this.goBack();
        });
      }
    });
  }

  navigateToRecord(): void {
    const record = this.linkedRecord();
    if (!record) return;
    const userId = this.common.getSelectedUserId();
    if (!this.sectionId || !userId) return;
    this.router.navigate(['/home', this.sectionId, 'users', userId, 'records', record.id]);
  }

  downloadMedia(): void {
    const m = this.media();
    if (!m) return;
    const s = m.data.storage;
    const url = `${s.serverRootURL}/${s.container}/${s.blobName}${s.blobSasToken}`;
    window.location.href = url;
  }

  private loadMedia(): void {
    this.loading.set(true);
    this.error.set(false);

    this.getMediaService.getMedia(this.mediaId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const docs = res as MediaDocument[];
        if (docs.length > 0) {
          this.media.set(docs[0]);
          this.popupWindowState.setCurrentRecordLabel(this.buildMentionLabel(docs[0]));
          this.findLinkedRecord();
          this.popupWindowState.show();
          this.loading.set(false);
          this.cdr.markForCheck();
          // ビデオの場合はプレイヤー初期化をスケジュール
          if (docs[0].data.mediaType === 'Video' && docs[0].data.mediaStatus === 'Encoded') {
            setTimeout(() => this.initializePlayer());
          }
        } else {
          this.error.set(true);
          this.loading.set(false);
        }
      },
      error: () => {
        this.error.set(true);
        this.loading.set(false);
      },
    });
  }

  private findLinkedRecord(): void {
    if (!this.mediaId) return;

    // メディア側の生データから mediaRecordId を取得（メディア → レコードの逆引き）
    const rawMedias: any[] = this.common.getUserMediasList() ?? [];
    const rawMedia = rawMedias.find((m: any) => m?.id === this.mediaId);
    const linkedRecordId: string | undefined = rawMedia?.data?.mediaRecordId;
    if (!linkedRecordId) {
      this.linkedRecord.set(null);
      return;
    }

    const rawRecords = this.common.getUserRecordsList();
    if (!rawRecords) {
      this.linkedRecord.set(null);
      return;
    }

    const converted = TypesUtils.convertJsonToRecords(rawRecords, false);
    const match = converted.records.find((r) => r.id === linkedRecordId);
    this.linkedRecord.set(match ?? null);
  }

  private initializePlayer(): void {
    const m = this.media();
    const el = this.mkplayerdiv?.nativeElement;
    if (!m?.data?.storage?.streamingURL || !el) return;

    this.destroyPlayer();

    try {
      this.playerInstance = createMkplayerInstance(
        el,
        m.data.storage.streamingURL,
        m.data.storage.streamingToken,
        {
          onError: () => this.handlePlayerLoadFailed(),
          // 「実際に再生可能」になって初めてロード成功とみなす。<video> 挿入だけでは止めない。
          onReady: () => {
            this.clearPlayerTimer();
            this.playerRetryCount = 0;
          },
        },
      );
      this.cdr.markForCheck();

      // <video> が挿入されても duration 未確定/readyState 低のまま固まる DRM ケースもリトライ対象。
      this.clearPlayerTimer();
      this.playerTimer = setTimeout(() => {
        const dur = this.playerInstance?.getDuration?.() ?? 0;
        const video = el.querySelector('video') as HTMLVideoElement | null;
        const ready = dur > 0 || (video?.readyState ?? 0) >= 2;
        if (!ready) {
          this.handlePlayerLoadFailed();
        }
      }, PLAYER_LOAD_TIMEOUT_MS);
    } catch {
      // MKPlayer 初期化失敗
    }
  }

  private handlePlayerLoadFailed(): void {
    this.destroyPlayer();
    this.playerRetryCount++;
    if (this.playerRetryCount > PLAYER_RETRY_MAX) return;

    this.playerTimer = setTimeout(() => {
      this.initializePlayer();
    }, PLAYER_RETRY_INTERVAL_MS);
  }

  private destroyPlayer(): void {
    this.clearPlayerTimer();
    if (this.playerInstance) {
      destroyMkplayerInstance(this.playerInstance);
      this.playerInstance = null;
    }
  }

  private clearPlayerTimer(): void {
    if (this.playerTimer) {
      clearTimeout(this.playerTimer);
      this.playerTimer = null;
    }
  }

  private buildMentionLabel(media: MediaDocument): string {
    const method = media?.data?.method ?? 'Others';
    const createdAt = this.toMentionDateTime(media?.data?.createdAt);
    const videoLabel = $localize`:@@home.mediaDetail.mention.video:動画`;
    const imageLabel = $localize`:@@home.mediaDetail.mention.image:静止画`;
    if (media?.data?.mediaType === 'Video') {
      return [method, videoLabel, createdAt].filter(Boolean).join(' ');
    }
    return [imageLabel, method, createdAt].filter(Boolean).join(' ');
  }

  private toMentionDateTime(isoDate: string | undefined): string {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }
}
